import { useState, useCallback, useMemo } from 'react';
import api from '@/utils/api';
import { toastMessage } from '@/utils/toast-message';
import { RefreshCw, GitBranch, Users, ShoppingCart, Repeat, ChevronDown, X } from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────

interface RRRow {
  assigned_user_name: string;
  assigned_user_id: number;
  daily_lead_cap: number | null;
  daily_reorder_cap: number | null;
  abc: number;
  reorder: number;
  scheduler: number;
  total: number;
}

interface DailyRow {
  day: string;
  abc: number;
  reorder: number;
  total: number;
}

interface Totals {
  abc: number;
  reorder: number;
  scheduler: number;
  total: number;
  active_users: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const today = () => new Date().toISOString().split('T')[0];
const daysAgo = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().split('T')[0];
};
const num = (v: number) => v.toLocaleString('en-IN');
const cap = (v: number | null) => (v == null || v === 0 ? '—' : String(v));

// ─── Card ─────────────────────────────────────────────────────────────────────

const Card = ({ label, value, icon, color }: {
  label: string; value: number;
  icon: React.ReactNode; color: string;
}) => (
  <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2.5 flex items-center gap-2.5">
    <div className={`p-1.5 rounded-md flex-shrink-0 ${color}`}>{icon}</div>
    <div className="min-w-0">
      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{label}</p>
      <p className="text-lg font-bold text-gray-900 dark:text-white leading-tight">{num(value)}</p>
    </div>
  </div>
);

// ─── Agent Multi-Select ───────────────────────────────────────────────────────

const AgentFilter = ({ agents, selected, onChange }: {
  agents: string[];
  selected: Set<string>;
  onChange: (s: Set<string>) => void;
}) => {
  const [open, setOpen] = useState(false);

  const toggle = (name: string) => {
    const next = new Set(selected);
    if (next.has(name)) next.delete(name); else next.add(name);
    onChange(next);
  };

  const label = selected.size === 0
    ? 'All Agents'
    : selected.size === 1
      ? [...selected][0]
      : `${selected.size} agents selected`;

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-2 px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 min-w-[160px] justify-between"
      >
        <span className="truncate">{label}</span>
        <div className="flex items-center gap-1 flex-shrink-0">
          {selected.size > 0 && (
            <X
              className="h-3.5 w-3.5 text-gray-400 hover:text-red-500"
              onClick={e => { e.stopPropagation(); onChange(new Set()); }}
            />
          )}
          <ChevronDown className="h-3.5 w-3.5 text-gray-400" />
        </div>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 z-20 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg w-56 max-h-72 overflow-y-auto">
            {agents.map(name => (
              <label key={name} className="flex items-center gap-2.5 px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer text-sm text-gray-700 dark:text-gray-200">
                <input
                  type="checkbox"
                  checked={selected.has(name)}
                  onChange={() => toggle(name)}
                  className="rounded border-gray-300"
                />
                <span className="truncate">{name}</span>
              </label>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const RoundRobinLogs = () => {
  const [fromDate, setFromDate] = useState(daysAgo(6));
  const [toDate, setToDate] = useState(today());
  const [rows, setRows] = useState<RRRow[]>([]);
  const [daily, setDaily] = useState<DailyRow[]>([]);
  const [totals, setTotals] = useState<Totals | null>(null);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [selectedAgents, setSelectedAgents] = useState<Set<string>>(new Set());

  const fetchData = useCallback(async () => {
    setLoading(true);
    setSelectedAgents(new Set());
    try {
      const res = await api.req({
        url: '/round-robin-logs/summary',
        method: api.Method.POST,
        data: { from_date: fromDate, to_date: toDate },
      }) as unknown as { data: { rows: RRRow[]; totals: Totals; daily: DailyRow[] } };
      setRows(res.data?.rows || []);
      setTotals(res.data?.totals || null);
      setDaily(res.data?.daily || []);
      setLoaded(true);
    } catch {
      toastMessage('Failed to load RR logs', 'error');
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  // Agent list for the filter dropdown
  const agentNames = useMemo(() => rows.map(r => r.assigned_user_name), [rows]);

  // Filtered rows
  const visibleRows = useMemo(() =>
    selectedAgents.size === 0 ? rows : rows.filter(r => selectedAgents.has(r.assigned_user_name)),
    [rows, selectedAgents]
  );

  // Filtered totals
  const filteredTotals = useMemo(() => ({
    abc: visibleRows.reduce((s, r) => s + r.abc, 0),
    reorder: visibleRows.reduce((s, r) => s + r.reorder, 0),
    total: visibleRows.reduce((s, r) => s + r.total, 0),
  }), [visibleRows]);

  const t = totals;

  return (
    <div className="flex flex-col h-full bg-gray-50 dark:bg-gray-900">
      {/* ── Header ── */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="bg-violet-500 p-2 rounded-lg">
              <GitBranch className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Round Robin Logs</h1>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Assignment history — ABC leads &amp; reorder leads per agent
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            {loaded && (
              <AgentFilter
                agents={agentNames}
                selected={selectedAgents}
                onChange={setSelectedAgents}
              />
            )}
            <div className="flex items-center space-x-2">
              <label className="text-xs text-gray-500 dark:text-gray-400">From</label>
              <input
                type="date"
                value={fromDate}
                max={toDate}
                onChange={e => setFromDate(e.target.value)}
                className="text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-brandColor"
              />
            </div>
            <div className="flex items-center space-x-2">
              <label className="text-xs text-gray-500 dark:text-gray-400">To</label>
              <input
                type="date"
                value={toDate}
                min={fromDate}
                onChange={e => setToDate(e.target.value)}
                className="text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-brandColor"
              />
            </div>
            <button
              onClick={fetchData}
              disabled={loading}
              className="flex items-center space-x-2 px-4 py-1.5 bg-brandColor text-white rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50 transition-opacity"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              <span>{loading ? 'Loading…' : 'Run'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* ── Body ── */}
      <div className="flex-1 flex flex-col min-h-0 p-6 gap-4">

        {/* ── Cards ── */}
        {t && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card label="Total Assigned" value={t.total} icon={<GitBranch className="h-3.5 w-3.5 text-violet-600" />} color="bg-violet-50 dark:bg-violet-900/20" />
            <Card label="ABC Leads" value={t.abc} icon={<ShoppingCart className="h-3.5 w-3.5 text-blue-600" />} color="bg-blue-50 dark:bg-blue-900/20" />
            <Card label="Reorder Leads" value={t.reorder} icon={<Repeat className="h-3.5 w-3.5 text-teal-600" />} color="bg-teal-50 dark:bg-teal-900/20" />
            <Card label="Active Users" value={t.active_users} icon={<Users className="h-3.5 w-3.5 text-orange-600" />} color="bg-orange-50 dark:bg-orange-900/20" />
          </div>
        )}

        {/* ── Note ── */}
        {loaded && (
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg px-4 py-2.5 text-xs text-amber-800 dark:text-amber-300 flex flex-wrap gap-x-6 gap-y-1">
            <span><strong>ABC ≠ Reorder</strong> — different pipelines: ABC = GoKwik/Shopify webhook intake, Reorder = Clickpost delivery (code 8/48). Volume depends on how many carts were abandoned vs orders delivered.</span>
            <span><strong>Counts are RR engine assignments only</strong> — manual reassignments and leads where all agents were at cap are not logged here.</span>
          </div>
        )}

        {/* ── Empty state ── */}
        {!loaded && !loading && (
          <div className="flex flex-col items-center justify-center py-24 text-gray-400">
            <GitBranch className="h-10 w-10 mb-3 opacity-30" />
            <p className="text-sm">Select a date range and click <strong>Run</strong></p>
          </div>
        )}

        {loaded && (
          <div className="flex gap-5 flex-1 min-h-0">

            {/* ── Per-user table ── */}
            <div className="flex-1 min-h-0 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden flex flex-col">
              <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Per Agent
                  {selectedAgents.size > 0 && (
                    <span className="ml-2 text-xs font-normal text-gray-400">({visibleRows.length} of {rows.length})</span>
                  )}
                </p>
              </div>
              <div className="overflow-auto flex-1">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 z-10">
                    <tr className="border-b-2 border-gray-200 dark:border-gray-700 text-xs font-medium bg-gray-50 dark:bg-gray-900">
                      <th className="px-3 py-2.5 text-left text-gray-500 dark:text-gray-400">#</th>
                      <th className="px-4 py-2.5 text-left text-gray-500 dark:text-gray-400">Name</th>
                      <th className="px-3 py-2.5 text-center text-blue-600 dark:text-blue-400">ABC</th>
                      <th className="px-3 py-2.5 text-center text-gray-400 dark:text-gray-500 text-xs">Lead Cap</th>
                      <th className="px-3 py-2.5 text-center text-teal-600 dark:text-teal-400">Reorder</th>
                      <th className="px-3 py-2.5 text-center text-gray-400 dark:text-gray-500 text-xs">Reorder Cap</th>
                      <th className="px-3 py-2.5 text-center text-violet-700 dark:text-violet-300 font-semibold">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {visibleRows.map((r, i) => (
                      <tr key={r.assigned_user_name} className={`hover:bg-gray-50 dark:hover:bg-gray-700/30 ${i % 2 === 0 ? '' : 'bg-gray-50/40 dark:bg-gray-800/50'}`}>
                        <td className="px-3 py-2.5 text-xs text-gray-400">{i + 1}</td>
                        <td className="px-4 py-2.5 font-medium text-gray-900 dark:text-white whitespace-nowrap">{r.assigned_user_name}</td>
                        <td className="px-3 py-2.5 text-center">
                          <span className="inline-block min-w-[2.5rem] text-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300">
                            {num(r.abc)}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 text-center text-xs text-gray-400 dark:text-gray-500">
                          {cap(r.daily_lead_cap)}
                        </td>
                        <td className="px-3 py-2.5 text-center">
                          <span className="inline-block min-w-[2.5rem] text-center px-1.5 py-0.5 rounded text-xs font-medium bg-teal-50 text-teal-700 dark:bg-teal-900/20 dark:text-teal-300">
                            {num(r.reorder)}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 text-center text-xs text-gray-400 dark:text-gray-500">
                          {cap(r.daily_reorder_cap)}
                        </td>
                        <td className="px-3 py-2.5 text-center">
                          <span className="inline-block min-w-[2.5rem] text-center px-1.5 py-0.5 rounded text-xs font-semibold bg-violet-50 text-violet-700 dark:bg-violet-900/20 dark:text-violet-300">
                            {num(r.total)}
                          </span>
                        </td>
                      </tr>
                    ))}
                    {/* Totals row */}
                    {visibleRows.length > 1 && (
                      <tr className="bg-gray-100 dark:bg-gray-700 border-t-2 border-gray-300 dark:border-gray-500 font-semibold">
                        <td className="px-3 py-3 text-xs font-bold uppercase tracking-wide text-gray-500" colSpan={2}>
                          {selectedAgents.size > 0 ? `SUBTOTAL (${visibleRows.length})` : 'TOTAL'}
                        </td>
                        <td className="px-3 py-3 text-center text-sm text-blue-700 dark:text-blue-300">{num(filteredTotals.abc)}</td>
                        <td />
                        <td className="px-3 py-3 text-center text-sm text-teal-700 dark:text-teal-300">{num(filteredTotals.reorder)}</td>
                        <td />
                        <td className="px-3 py-3 text-center text-sm text-violet-700 dark:text-violet-300">{num(filteredTotals.total)}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
                {visibleRows.length === 0 && (
                  <div className="text-center py-12 text-gray-400 text-sm">No data for selected range</div>
                )}
              </div>
            </div>

            {/* ── Daily breakdown ── */}
            <div className="w-64 min-h-0 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden flex flex-col flex-shrink-0">
              <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
                <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">Daily</p>
              </div>
              <div className="overflow-auto flex-1">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 z-10">
                    <tr className="border-b-2 border-gray-200 dark:border-gray-700 text-xs font-medium bg-gray-50 dark:bg-gray-900">
                      <th className="px-4 py-2.5 text-left text-gray-500 dark:text-gray-400">Date</th>
                      <th className="px-2 py-2.5 text-center text-blue-600 dark:text-blue-400">ABC</th>
                      <th className="px-2 py-2.5 text-center text-teal-600 dark:text-teal-400">Reorder</th>
                      <th className="px-2 py-2.5 text-center text-violet-700 dark:text-violet-300 font-semibold">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {daily.map((d, i) => (
                      <tr key={d.day} className={`hover:bg-gray-50 dark:hover:bg-gray-700/30 ${i % 2 === 0 ? '' : 'bg-gray-50/40 dark:bg-gray-800/50'}`}>
                        <td className="px-4 py-2.5 text-xs font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">{d.day}</td>
                        <td className="px-2 py-2.5 text-center text-xs text-blue-600 dark:text-blue-400">{num(d.abc)}</td>
                        <td className="px-2 py-2.5 text-center text-xs text-teal-600 dark:text-teal-400">{num(d.reorder)}</td>
                        <td className="px-2 py-2.5 text-center text-xs font-semibold text-violet-700 dark:text-violet-300">{num(d.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {daily.length === 0 && (
                  <div className="text-center py-12 text-gray-400 text-sm">No data</div>
                )}
              </div>
            </div>

          </div>
        )}
      </div>
    </div>
  );
};

export default RoundRobinLogs;
